import React, {useCallback, useEffect, useState, useMemo, useRef, useLayoutEffect} from 'react';
import {FormattedMessage, useIntl} from 'react-intl';
import DOMPurify from 'dompurify';
import {MatrixEvent, MatrixEventEvent, RoomEvent, RoomMember, EventStatus, type Room} from 'matrix-js-sdk';
import EmojiPicker, {EmojiStyle, Theme} from 'emoji-picker-react';
import {AuthenticatedImage} from "./AuthenticatedImage";
import {PhoneHQDropdown, PhoneHQTooltip, PhoneHQGlyph} from 'ch3-ui-lib';
import {MatrixAvatar} from "./MatrixAvatar.tsx";
import {DeleteMessageDialog} from "./DeleteMessageDialog.tsx";
import {Reply, Smile, ExternalLink, MessageSquareLock, MoreVertical, Pencil, Trash2, Forward, ChevronDown, ChevronUp} from 'lucide-react';
import {AuthenticatedVideo} from "./AuthenticatedVideo.tsx";
import {VideoReplyPreview} from "./VideoReplyPreview.tsx";
import {AuthenticatedAudio} from "./AuthenticatedAudio.tsx";
import {AuthenticatedFile} from "./AuthenticatedFile.tsx";
import {useMatrixContext} from "../../providers/MatrixProvider.tsx";
import {extractUrls, useGetLinkPreviewMutation, type LinkPreview} from '../../services/linkPreviewService';
import {ImageGrid} from "./ImageGrid.tsx";
import {ImageInfo} from "./chatTypes.ts";
import {ImageGallery} from "./ImageGallery.tsx";
import {MessageTextContent, type DecryptionTooltipProps} from "./MessageTextContent.tsx";
import {getMediaCaption, getMediaFormattedCaption, isMediaMessageType} from "./mediaMessageUtils.ts";
import {useAiRequest} from './useAiRequest';
import {aiStatusText} from '../../utils/aiRequestProtocol';
import TranscriptInline from "./TranscriptModal.tsx";
import {matrixService} from "../../services/matrixService.ts";
import {isCallInviteEvent} from "../../utilis/callTimeline.ts";
import {isRtcNotificationEvent} from "../../utilis/rtcNotification.ts";
import {useIsAiBotEnabledQuery} from "../../services/chatService.ts";
import {canRequestAudioTranscription, isBotUser, isServiceParticipant, isWebhookMessage} from "../../utils/aiBotUtils.ts";
import {getTranscriptionMessageData, isTranscriptionMessageContent} from "../../utilis/chatUtils.ts";
import {getMatrixMentionTarget} from "../../utils/mentionUtils.ts";
import {isEditableMessage} from "../../utils/messageEdits.ts";
import {isTooLargeSendError} from "../../utils/messageSizeLimit.ts";
import {canPrivateReply} from "../../utils/privateReplyQuote.ts";
import {
    annotateCodeBlocksForCollapse,
    CODE_BLOCK_EXPAND_TESTID,
    createCodeBlockExpandButton,
    sizeCollapsedCodeBlock,
} from "../../utils/codeBlockCollapse.ts";
import {CodeBlockViewer} from "./CodeBlockViewer.tsx";
import VoiceMessagePlayer from "./VoiceMessagePlayer.tsx";
import {forwardedLabel, isForwardableContent, readForwardedFrom} from "../../utils/forwardMessage.ts";

// Quick reactions in the order of D-CHAT-01/S04: ❤️ 😮 😂 😢 🙏 👍 (unified codes).
export const QUICK_REACTIONS = ['2764-fe0f', '1f62e', '1f602', '1f622', '1f64f', '1f44d'];

interface MessageBubbleProps {
    message: MatrixEvent;
    roomId: string;
    onReply?: (messageId: string) => void;
    onEdit?: (messageId: string) => void;
    onPrivateReply?: (message: MatrixEvent) => void;
    onForward?: (message: MatrixEvent) => void;
    /** Opens the original of a forwarded message when the reader is in that room. */
    onOpenForwardedOriginal?: (roomId: string, eventId: string) => void;
    sendReaction: (roomId: string, eventId: string, emoji: string) => Promise<void>;
    removeReaction: (roomId: string, eventId: string, emoji: string) => Promise<void>;
    getClient: () => any;
    getCurrentUserId: () => string | null;
    highlightedMessageId?: string | undefined | null;
    scrollToEvent: (eventId: string) => void;
    showTimestamp?: boolean;
    showAvatar?: boolean;
    isFirstInGroup?: boolean;
    isLastInGroup?: boolean;
    isGroupChat?: boolean;
    isAiBotInRoom: boolean;
    isRoomEncrypted: boolean;
    aiBotJoinedAt: number | null;
    searchQuery?: string;
    imageGroup?: MatrixEvent[];
    onOpenImageGallery?: (images: ImageInfo[], startIndex: number) => void;
    onMediaLoad?: () => void;
    onViewProfile?: (matrixUserId: string) => void;
    onOpenRoomMention?: (roomIdOrAlias: string) => void;
    onUnresolvedMention?: () => void;
}

export interface UserInfo {
    avatarUrl: string | null;
    displayName: string;
    rawDisplayName: string;
    userId: string;
}

interface ReadByUser {
    userId: string;
    displayName: string;
    rawDisplayName: string;
    timestamp: number;
    avatarUrl?: string;
}

interface ReplyInfo {
    originalMessageId: string;
    originalSender: string;
    originalContent: string | React.ReactNode;
    originalSenderName: string;
    originalMsgType?: string;
    originalIsEdited: boolean;
}

interface Reaction {
    emoji: string;
    count: number;
    users: string[];
    userNames: string[];
    hasReacted: boolean;
    eventIds: string[];
}

export const groupMessages = (messages: MatrixEvent[]): MatrixEvent[][] => {
    if (messages.length === 0) return [];

    const groups: MatrixEvent[][] = [];
    let currentGroup: MatrixEvent[] = [messages[0]];

    const isSystemEvent = (message: MatrixEvent) => {
        const eventType = message.getType();
        return eventType === 'm.room.member' ||
            eventType === 'm.room.name' ||
            eventType === 'm.room.topic' ||
            // A call stands on its own, the way the design draws it, rather
            // than folding into the message group it happens to sit beside.
            isCallInviteEvent(message) ||
            isRtcNotificationEvent(message);
    };

    for (let i = 1; i < messages.length; i++) {
        const currentMessage = messages[i];
        const previousMessage = messages[i - 1];

        if (isSystemEvent(currentMessage) || isSystemEvent(previousMessage)) {
            groups.push(currentGroup);
            currentGroup = [currentMessage];
            continue;
        }

        const currentSender = currentMessage.getSender();
        const previousSender = previousMessage.getSender();
        const currentTime = currentMessage.getTs();
        const previousTime = previousMessage.getTs();


        const timeDifference = currentTime - previousTime;
        const sameGroup = currentSender === previousSender && timeDifference <= 60000;

        if (sameGroup) {
            currentGroup.push(currentMessage);
        } else {
            groups.push(currentGroup);
            currentGroup = [currentMessage];
        }
    }


    groups.push(currentGroup);

    return groups;
};

const MessageBubble: React.FC<MessageBubbleProps> = React.memo(({
                                                                    message,
                                                                    roomId,
                                                                    onReply,
                                                                    onEdit,
                                                                    onPrivateReply,
                                                                    onForward,
                                                                    onOpenForwardedOriginal,
                                                                    sendReaction,
                                                                    removeReaction,
                                                                    getClient,
                                                                    getCurrentUserId,
                                                                    highlightedMessageId,
                                                                    scrollToEvent,
                                                                    showTimestamp = true,
                                                                    isFirstInGroup = true,
                                                                    isLastInGroup = true,
                                                                    isGroupChat = false,
                                                                    isAiBotInRoom,
                                                                    aiBotJoinedAt,
                                                                    searchQuery,
                                                                    imageGroup,
                                                                    onOpenImageGallery,
                                                                    onMediaLoad,
                                                                    onViewProfile,
                                                                    onOpenRoomMention,
                                                                    onUnresolvedMention,
                                                                }) => {
    const intl = useIntl();
    const [showEmojiPicker, setShowEmojiPicker] = useState(false);
    const [singleImageGallery, setSingleImageGallery] = useState<ImageInfo[] | null>(null);
    const [isReacting, setIsReacting] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [userInfo, setUserInfo] = useState<UserInfo | null>(null);
    const [replyInfo, setReplyInfo] = useState<ReplyInfo | null>(null);
    const [reactions, setReactions] = useState<Reaction[]>([]);
    const [linkPreviews, setLinkPreviews] = useState<LinkPreview[]>([]);
    const [loadingPreviews, setLoadingPreviews] = useState(false);
    const [getLinkPreview] = useGetLinkPreviewMutation();
    const [readByUsers, setReadByUsers] = useState<ReadByUser[]>([]);
    const [emojiPickerPosition, setEmojiPickerPosition] = useState<'top' | 'bottom'>('bottom');
    const [transcriptText, setTranscriptText] = useState<string | null>(null);
    const [showTranscriptModal, setShowTranscriptModal] = useState(false);
    const [legacyTranscriptError, setLegacyTranscriptError] = useState<string | null>(null);
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);
    const [expandedCodeBlock, setExpandedCodeBlock] = useState<string | null>(null);
    const [isRedacted, setIsRedacted] = useState(() => message.isRedacted());

    const messageBubbleRef = useRef<HTMLDivElement>(null);
    const {getLastMessageIdForSelectedRoom} = useMatrixContext();
    const {data: isAiBotEnabled} = useIsAiBotEnabledQuery();

    const [replacingEventId, setReplacingEventId] = useState<string | undefined>(() => message.replacingEventId());

    useEffect(() => {
        const syncReplacement = () => setReplacingEventId(message.replacingEventId());
        syncReplacement();
        message.on(MatrixEventEvent.Replaced, syncReplacement);
        return () => {
            message.off(MatrixEventEvent.Replaced, syncReplacement);
        };
    }, [message]);

    const [eventId, setEventId] = useState(() => message.getId());

    useEffect(() => {
        const syncEventId = () => setEventId(message.getId());
        syncEventId();
        message.on(MatrixEventEvent.LocalEventIdReplaced, syncEventId);
        message.on(MatrixEventEvent.Status, syncEventId);
        return () => {
            message.off(MatrixEventEvent.LocalEventIdReplaced, syncEventId);
            message.off(MatrixEventEvent.Status, syncEventId);
        };
    }, [message]);

    const [replacementRevision, setReplacementRevision] = useState(0);

    useEffect(() => {
        const replacement = message.replacingEvent();
        if (!replacement) return;
        const refresh = () => setReplacementRevision(revision => revision + 1);
        if (replacement.isEncrypted() && replacement.getClearContent()) refresh();
        replacement.on(MatrixEventEvent.Decrypted, refresh);
        return () => {
            replacement.off(MatrixEventEvent.Decrypted, refresh);
        };
    }, [message, replacingEventId]);

    const isEdited = replacingEventId !== undefined;

    const content = useMemo(() => {
        void replacingEventId;
        void replacementRevision;
        return message.getContent();
    }, [message, replacingEventId, replacementRevision]);
    const currentUserId = useMemo(() => getCurrentUserId(), [getCurrentUserId]);

    const codeBlockContentKey = `${typeof content.body === 'string' ? content.body : ''}|${typeof content.formatted_body === 'string' ? content.formatted_body : ''}`;

    useLayoutEffect(() => {
        const container = messageBubbleRef.current;
        if (!container || !container.querySelector('.code-block-wrapper')) return;

        const codeBlockExpandLabel = intl.formatMessage({id: 'chat.code-block.expand', defaultMessage: 'Expand'});

        const checkBlock = (pre: HTMLElement) => {
            const wrapper = pre.closest<HTMLElement>('.code-block-wrapper');
            if (!wrapper) return;

            if (pre.classList.contains('code-block-collapsed')) {
                sizeCollapsedCodeBlock(pre);
                return;
            }

            const existingButton = wrapper.querySelector(`[data-testid="${CODE_BLOCK_EXPAND_TESTID}"]`);
            const isWide = pre.scrollWidth > pre.clientWidth;

            if (isWide && !existingButton) {
                wrapper.appendChild(createCodeBlockExpandButton(document, codeBlockExpandLabel));
            } else if (!isWide && existingButton) {
                existingButton.remove();
            }
        };

        const pres = Array.from(container.querySelectorAll<HTMLElement>('.code-block-wrapper pre'));
        pres.forEach(checkBlock);

        if (typeof ResizeObserver === 'undefined') return;

        const resizeObserver = new ResizeObserver(() => pres.forEach(checkBlock));
        pres.forEach(pre => resizeObserver.observe(pre));
        return () => resizeObserver.disconnect();
    }, [codeBlockContentKey, intl, userInfo]);

    const handleCodeBlockClick = useCallback((event: React.MouseEvent<HTMLDivElement>) => {
        const target = event.target;
        if (!(target instanceof Element)) return;

        const expandButton = target.closest(`[data-testid="${CODE_BLOCK_EXPAND_TESTID}"]`);
        if (!expandButton) return;

        const pre = expandButton.closest('.code-block-wrapper')?.querySelector('pre');
        setExpandedCodeBlock(pre?.textContent ?? '');
    }, []);
    const transcription = useAiRequest({roomId, currentUserId: currentUserId ?? '', kind: 'transcription',
        audioEventId: message.getId(), enabled: Boolean(isAiBotEnabled && isAiBotInRoom && content.msgtype === 'm.audio')});
    const isTranscribing = transcription.pending || transcription.sending;
    const transcriptError = transcription.transportError ?? legacyTranscriptError
        ?? (transcription.status?.state === 'failed' ? aiStatusText(transcription.status) : null);
    const isOwnMessage = useMemo(() => message.getSender() === currentUserId, [message, currentUserId]);
    const canRequestTranscript = useMemo(
        () => canRequestAudioTranscription(message.getTs(), aiBotJoinedAt),
        [message, aiBotJoinedAt],
    );
    const isDecryptionErrorMessage = useMemo(() => {
        const isSdkDecryptionFailure = typeof message.isDecryptionFailure === 'function' && message.isDecryptionFailure();
        const isBadEncryptedType = content.msgtype === 'm.bad.encrypted';

        return isSdkDecryptionFailure || isBadEncryptedType;
    }, [content, message]);
    const displayBody = useMemo(() => {
        if (isDecryptionErrorMessage) {
            return 'Unable to decrypt';
        }

        return typeof content.body === 'string' ? content.body : '';
    }, [content, isDecryptionErrorMessage]);
    const decryptionErrorTooltipText = useMemo(() => {
        const detailedErrorText = typeof content.body === 'string' ? content.body.trim() : '';

        if (isDecryptionErrorMessage && detailedErrorText && detailedErrorText !== 'Unable to decrypt') {
            const cleaned = detailedErrorText
                .replace(/^\*+\s*/, '')
                .replace(/\s*\*+$/, '')
                .replace(/^(?:(?:Unable to decrypt|DecryptionError):?\s*)+/i, '')
                .trim();

            return cleaned || 'decryptionError';
        }

        const reason = message.decryptionFailureReason;

        if (reason) {
            return reason;
        }

        return 'decryptionError';
    }, [content, message, isDecryptionErrorMessage]);
    const decryptionErrorTooltipProps = useMemo<DecryptionTooltipProps>(
        () => isOwnMessage
            ? {'data-tooltip-left': decryptionErrorTooltipText}
            : {'data-tooltip-right': decryptionErrorTooltipText},
        [isOwnMessage, decryptionErrorTooltipText]
    );

    const lastMessageId = getLastMessageIdForSelectedRoom();
    const txnId = message.getUnsigned()?.transaction_id;
    const isLastMessage = lastMessageId && (lastMessageId === message.getId() || (txnId && lastMessageId === txnId));

    const highlightSearchTerm = useCallback((text: string) => {
        if (!searchQuery?.trim() || !text) return text;

        const regex = new RegExp(`(${searchQuery.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
        const parts = text.split(regex);

        return parts.map((part, index) =>
            regex.test(part) ? (
                <mark key={index} className={`text-brand-ink bg-transparent font-semibold` }>
                    {part}
                </mark>
            ) : part
        );
    }, [searchQuery]);

    useEffect(() => {
        let cancelled = false;

        const fetchPreviews = async () => {
            const { msgtype, body } = content;

            if (msgtype !== 'm.text' || !body || isWebhookMessage(message)) {
                setLinkPreviews(prev => (prev.length === 0 ? prev : []));
                setLoadingPreviews(false);
                return;
            }

            const urls = extractUrls(body);
            if (urls.length === 0) {
                setLinkPreviews(prev => (prev.length === 0 ? prev : []));
                setLoadingPreviews(false);
                return;
            }

            setLoadingPreviews(true);

            try {
                const previewPromises = urls.slice(0, 2).map(url =>
                    getLinkPreview({ url }).unwrap()
                );

                const previews = await Promise.all(previewPromises);

                const successfulPreviews = previews.filter(
                    (preview): preview is LinkPreview => preview !== null && preview !== undefined
                );

                if (!cancelled) {
                    setLinkPreviews(successfulPreviews);
                }
            } catch (error) {
            } finally {
                if (!cancelled) {
                    setLoadingPreviews(false);
                }
            }
        };

        fetchPreviews();

        return () => {
            cancelled = true;
        };
    }, [content, getLinkPreview]);

    const renderTextWithMentions = useMemo(() => {
        return (text: string, sourceContent: Record<string, unknown> = content) => {
            const formattedBody = sourceContent.format === "org.matrix.custom.html" && typeof sourceContent.formatted_body === 'string'
                ? sourceContent.formatted_body
                : typeof sourceContent.msgtype === 'string' && isMediaMessageType(sourceContent.msgtype)
                    ? getMediaFormattedCaption(sourceContent)
                    : null;

            if (formattedBody) {
                const parser = new DOMParser();
                const doc = parser.parseFromString(formattedBody, 'text/html');

                annotateCodeBlocksForCollapse(doc, intl.formatMessage({id: 'chat.code-block.expand', defaultMessage: 'Expand'}));

                const mentionLinks = doc.querySelectorAll('a[href^="https://matrix.to/#/"]');
                mentionLinks.forEach(link => {
                    link.className = 'mention';
                    (link as HTMLElement).style.cssText = `
          color: var(--brand-ink);
          padding: 2px 6px;
          border-radius: 4px;
          text-decoration: none;
          font-weight: 500;
          cursor: pointer;
        `;
                });

                const regularLinks = doc.querySelectorAll('a:not([href^="https://matrix.to/#/"])');
                regularLinks.forEach(link => {
                    link.className = 'text-link';
                    (link as HTMLElement).style.cssText = `
          color: var(--brand-ink);
          text-decoration: underline;
          cursor: pointer;
          font-weight: 500;
        `;
                    link.setAttribute('target', '_blank');
                    link.setAttribute('rel', 'noopener noreferrer');
                });

                const handleFormattedBodyClick = (event: React.MouseEvent<HTMLSpanElement>) => {
                    const target = event.target;
                    if (!(target instanceof Element)) return;

                    const mentionLink = target.closest<HTMLAnchorElement>('a[href^="https://matrix.to/#/"]');
                    if (!mentionLink) return;

                    // Claim the click as soon as we know it is a mention. Bailing
                    // out before this left an unrecognised link to the browser,
                    // which is how a room mention we could not parse ended up
                    // doing nothing visible at all.
                    event.preventDefault();
                    event.stopPropagation();

                    // Prefer the literal attribute: the href property resolves and
                    // re-encodes, which can reshape an alias mention.
                    const rawHref = mentionLink.getAttribute('href') || mentionLink.href;
                    const mentionTarget = getMatrixMentionTarget(rawHref)
                        ?? getMatrixMentionTarget(mentionLink.href);

                    if (!mentionTarget) {
                        onUnresolvedMention?.();
                        return;
                    }

                    // A room mention is a shortcut into that conversation, not a
                    // request to inspect it — only user mentions open a profile.
                    if (mentionTarget.kind === 'room') {
                        onOpenRoomMention?.(mentionTarget.id);
                        return;
                    }

                    onViewProfile?.(mentionTarget.id);
                };

                return (
                    <span
                        onClick={handleFormattedBodyClick}
                        dangerouslySetInnerHTML={{
                            __html: DOMPurify.sanitize(doc.body.innerHTML, {ADD_ATTR: ['target']})
                        }}
                    />
                );
            }
            return <span>{text}</span>;
        };
    }, [content, isOwnMessage, onViewProfile, onOpenRoomMention, onUnresolvedMention, intl]);

    const renderDeliveryStatus = useCallback((status: EventStatus ) => {
        const showStatusIcon = !readByUsers.length && isOwnMessage && isLastMessage;
        const hasReadReceipts = readByUsers.length > 0;

        if (!showStatusIcon && !hasReadReceipts) return null;

        const notSentStatus = isTooLargeSendError(message.error)
            ? <div className="h-5 text-xxs text-danger-ink" data-tooltip={intl.formatMessage({id: 'chat.composer.too-long.failed-tooltip', defaultMessage: 'Message is too long to send. Shorten it or send it as a file.'})}>{intl.formatMessage({id: 'chat.composer.too-long.failed-label', defaultMessage: 'Too long'})}</div>
            : <div className="h-5 text-xxs text-danger-ink" data-tooltip="Failed to send">Failed</div>;

        const statusIcons: Partial<Record<EventStatus, React.ReactElement>> = {
            [EventStatus.SENDING]: <div className="h-5 text-ink-muted text-xxs" data-tooltip="Sending...">Sending...</div>,
            [EventStatus.SENT]: <div className="h-5  text-ink-muted text-xxs" data-tooltip="Sent">Delivered</div>,
            [EventStatus.NOT_SENT]: notSentStatus,
            [EventStatus.ENCRYPTING]: <div className="h-5 text-ink-muted text-xxs" data-tooltip="Encrypting...">Encrypting...</div>
        };
        return (
            <div className={`${isOwnMessage ? '' : 'ml-7s'} h-5 flex items-center justify-end mt-1 space-x-1`}>
                {showStatusIcon && statusIcons[status]}
                {hasReadReceipts && (
                    <div className="flex items-center space-x-1 ml-2">
                        <div className="flex -space-x-1 gap-1">
                            {readByUsers.slice(0, 3).map((user, index) => (
                                <PhoneHQTooltip key={user.userId} content={user.displayName} side="top"><div
                                        className="relative"
                                        style={{ zIndex: readByUsers.length - index }}
                                    >
                                        <MatrixAvatar
                                            name={user.rawDisplayName}
                                            mxcUrl={user.avatarUrl}
                                            size={'14px'}
                                            className="border border-white rounded-full"
                                        />
                                    </div></PhoneHQTooltip>
                            ))}
                            {readByUsers.length > 3 && (
                                <PhoneHQTooltip content={readByUsers.slice(3).map(user => user.displayName).join(', ')} side="top"><div className="w-4 h-4 bg-zinc-400 rounded-full flex items-center justify-center text-white text-xxs border border-white">
                                        +{readByUsers.length - 3}
                                    </div></PhoneHQTooltip>
                            )}
                        </div>
                    </div>
                )}
            </div>
        );
    }, [readByUsers, isOwnMessage, isLastMessage, message, intl]);

    useEffect(() => {
        const getUserInfo = async () => {
            const senderId = message.getSender();
            if (!senderId) return;

            try {
                const client = getClient();
                if(!client) return;
                const room = client.getRoom(roomId);

                if (room) {
                    const roomMember = room.getMember(senderId);

                    if (roomMember) {
                        const avatarUrl = roomMember.getMxcAvatarUrl() || null;
                        const rawDisplayName = roomMember.rawDisplayName || senderId;

                        setUserInfo({
                            avatarUrl,
                            displayName: rawDisplayName,
                            rawDisplayName,
                            userId: senderId,
                        });
                    }
                }
            } catch (error) {
                const fallbackName = formatSenderName(senderId);
                setUserInfo({
                    avatarUrl: null,
                    displayName: fallbackName,
                    rawDisplayName: fallbackName,
                    userId: senderId,
                });
            }
        };

        getUserInfo();
    }, [message, roomId, getClient]);

    const quotedEventId: string | undefined =
        content['m.relates_to']?.['m.in_reply_to']?.event_id
        ?? message.getOriginalContent()?.['m.relates_to']?.['m.in_reply_to']?.event_id;

    const [quotedReplacingEventId, setQuotedReplacingEventId] = useState<string | undefined>(undefined);

    useEffect(() => {
        if (!quotedEventId) {
            setQuotedReplacingEventId(undefined);
            return;
        }

        const quotedEvent: MatrixEvent | undefined = getClient()?.getRoom(roomId)?.findEventById(quotedEventId);
        if (!quotedEvent) {
            setQuotedReplacingEventId(undefined);
            return;
        }

        const syncReplacement = () => setQuotedReplacingEventId(quotedEvent.replacingEventId());
        syncReplacement();
        quotedEvent.on(MatrixEventEvent.Replaced, syncReplacement);
        return () => {
            quotedEvent.off(MatrixEventEvent.Replaced, syncReplacement);
        };
    }, [quotedEventId, roomId, getClient]);

    useEffect(() => {
        const extractReplyInfo = async () => {
            const relatesTo = content['m.relates_to'] ?? message.getOriginalContent()?.['m.relates_to'];
            const inReplyTo = relatesTo?.['m.in_reply_to'];


            if (inReplyTo?.event_id) {
                try {
                    const client = getClient();
                    const room = client?.getRoom(roomId);

                    if (room) {
                        const originalEvent = room.findEventById(inReplyTo.event_id);

                        if (originalEvent) {
                            const originalContent = originalEvent.getContent();
                            const originalSender = originalEvent.getSender() || 'Unknown';
                            const originalMember = room.getMember(originalSender);
                            const originalSenderName = originalMember?.rawDisplayName || formatSenderName(originalSender);
                            const originalMediaUrl = originalContent.url || originalContent.file?.url || '';
                            const originalEncryptedFile = originalContent.file;
                            const originalMimeType = originalContent.info?.mimetype;
                            let originalText: string | React.ReactNode = originalContent.body || '';

                            if (originalContent.msgtype === 'm.image') {
                                originalText = (
                                    <AuthenticatedImage
                                        src={originalMediaUrl}
                                        alt={originalContent.body || 'Image'}
                                        className="max-h-12 rounded"
                                        encryptedFile={originalEncryptedFile}
                                        mimeType={originalMimeType}
                                    />
                                );
                            } else if (originalContent.msgtype === 'm.file') {
                                originalText = `📎 ${originalContent.filename || 'File'}`;
                            } else if (originalContent.msgtype === 'm.audio') {
                                originalText = (
                                    <AuthenticatedAudio
                                        src={originalMediaUrl}
                                        className="w-48 rounded"
                                        controls={true}
                                        showSpeedControl={false}
                                        preload="metadata"
                                        encryptedFile={originalEncryptedFile}
                                        mimeType={originalMimeType}
                                    />
                                );
                            } else if (originalContent.msgtype === 'm.video') {
                                originalText = (
                                    <VideoReplyPreview
                                        label={originalContent.filename || originalContent.body}
                                        thumbnailSrc={originalContent.info?.thumbnail_url || originalContent.info?.thumbnail_file?.url}
                                        thumbnailEncryptedFile={originalContent.info?.thumbnail_file}
                                    />
                                );
                            }

                            setReplyInfo({
                                originalMessageId: inReplyTo.event_id,
                                originalSender,
                                originalContent: originalText,
                                originalSenderName,
                                originalMsgType: originalContent.msgtype,
                                originalIsEdited: originalEvent.replacingEventId() !== undefined
                            });
                        }
                    }
                } catch (error) {
                }
            } else {
                setReplyInfo(null);
            }
        };

        extractReplyInfo();
    }, [content, roomId, getClient, quotedReplacingEventId, message]);

    useEffect(() => {
        const extractReactions = () => {
            try {
                const client = getClient();
                const room = client?.getRoom(roomId);

                if (!room) return;

                const messageId = message.getId();
                if (!messageId) return;

                const timeline = room.getLiveTimeline();
                const events = timeline.getEvents();

                const reactionEvents = events.filter((event: MatrixEvent) =>
                    event.getType() === 'm.reaction' &&
                    event.getContent()?.['m.relates_to']?.event_id === messageId &&
                    !event.isRedacted()
                );

                const reactionMap = new Map<string, {
                    count: number;
                    users: string[];
                    userNames: string[];
                    eventIds: string[];
                }>();

                reactionEvents.forEach((reactionEvent: MatrixEvent) => {
                    const reactionContent = reactionEvent.getContent();
                    const emoji = reactionContent?.['m.relates_to']?.key;
                    const sender = reactionEvent.getSender();
                    const eventId = reactionEvent.getId();

                    if (emoji && sender && eventId) {
                        if (!reactionMap.has(emoji)) {
                            reactionMap.set(emoji, {
                                count: 0,
                                users: [],
                                userNames: [],
                                eventIds: []
                            });
                        }

                        const reaction = reactionMap.get(emoji)!;
                        if (!reaction.users.includes(sender)) {
                            reaction.users.push(sender);
                            reaction.eventIds.push(eventId);
                            reaction.count++;

                            const member = room.getMember(sender);
                            const displayName = member?.rawDisplayName || formatSenderName(sender);
                            reaction.userNames.push(displayName);
                        }
                    }
                });

                const reactionsArray: Reaction[] = Array.from(reactionMap.entries()).map(([emoji, data]) => ({
                    emoji,
                    count: data.count,
                    users: data.users,
                    userNames: data.userNames,
                    hasReacted: data.users.includes(currentUserId || ''),
                    eventIds: data.eventIds
                }));

                setReactions(reactionsArray);

            } catch (error) {
                setReactions([]);
            }
        };

        extractReactions();

        const handleRoomTimeline = (event: MatrixEvent, room: any) => {
            if (room?.roomId === roomId) {
                const eventType = event.getType();
                const isReactionEvent = eventType === 'm.reaction';
                const isRedactionEvent = eventType === 'm.room.redaction';

                if (isReactionEvent) {
                    const targetEventId = event.getContent()?.['m.relates_to']?.event_id;
                    if (targetEventId === message.getId()) {
                        extractReactions();
                    }
                } else if (isRedactionEvent) {
                    extractReactions();
                    const eventsToCheck = imageGroup && imageGroup.length > 1 ? imageGroup : [message];
                    if (eventsToCheck.some(msg => msg.isRedacted())) {
                        setIsRedacted(true);
                    }
                }
            }

        };

        const client = getClient();
        if (client) {
            client.on('Room.timeline' as any, handleRoomTimeline);
        }

        return () => {
            if (client) {
                client.off('Room.timeline' as any, handleRoomTimeline);
            }
        };
    }, [message, roomId, currentUserId, getClient]);


    useEffect(() => {
        if (content.msgtype !== 'm.audio') return;
        const client = matrixService.getClient();
        const messageId = message.getId();
        if (!client || !messageId || !currentUserId) return;
        const scan = () => {
            const events = client.getRoom(roomId)?.getLiveTimeline().getEvents() ?? [];
            const transactionId = message.getUnsigned().transaction_id;
            const confirmed = messageId.startsWith('~') && transactionId
                ? events.find(event => event.getUnsigned().transaction_id === transactionId && event.getId()?.startsWith('$'))
                : undefined;
            const audioId = confirmed?.getId() ?? messageId;
            let text: string | null = null;
            let error: string | null = null;
            if (!message.isRedacted()) {
                for (const event of events) {
                    if (event.isRedacted() || !isBotUser(event.getSender() ?? '', currentUserId)) continue;
                    const candidate = event.getContent();
                    if (isTranscriptionMessageContent(candidate)) {
                        const data = getTranscriptionMessageData(candidate);
                        if (data?.audio_event_id !== audioId) continue;
                        if (typeof data.text === 'string' && data.text.trim()) text = data.text;
                        else if (data.error) error = 'The transcript could not be prepared.';
                        continue;
                    }
                    const relation = candidate['m.relates_to'];
                    const related = relation?.['m.in_reply_to']?.event_id === audioId
                        || (relation?.rel_type === 'm.thread' && relation.event_id === audioId);
                    if (!related || typeof candidate.body !== 'string') continue;
                    const body = candidate.body.replace(/^(?:>[^\n]*\n)+\s*/, '').trim();
                    if (body.startsWith('🦻')) text = body.replace(/^🦻\s*/, '').trim() || null;
                }
            }
            setTranscriptText(text);
            setLegacyTranscriptError(text ? null : error);
        };
        scan();
        const onTimeline = (_event: MatrixEvent, room?: Room) => { if (room?.roomId === roomId) scan(); };
        const onDecrypted = (event: MatrixEvent) => { if (event.getRoomId() === roomId) scan(); };
        const onRedaction = (_event: MatrixEvent, room: Room) => { if (room.roomId === roomId) scan(); };
        client.on(RoomEvent.Timeline, onTimeline);
        client.on(MatrixEventEvent.Decrypted, onDecrypted);
        client.on(RoomEvent.Redaction, onRedaction);
        return () => {
            client.off(RoomEvent.Timeline, onTimeline);
            client.off(MatrixEventEvent.Decrypted, onDecrypted);
            client.off(RoomEvent.Redaction, onRedaction);
        };
    }, [content.msgtype, message, roomId, currentUserId]);

    // Update read receipts when the message or room changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        const client = getClient();
        const messageId = message.getId();
        const currentUserId = getCurrentUserId();

        if (!client || !messageId) return;

        const updateReadStatus = () => {
            try {
                const room = client.getRoom(roomId);
                if (!room) return;

                // Don't show read receipts for messages that are still being sent
                const messageStatus = message.status;
                if (messageStatus === EventStatus.SENDING || messageStatus === EventStatus.ENCRYPTING) {
                    setReadByUsers([]);
                    return;
                }

                const roomMembers = room.getJoinedMembers();
                const readUsers: ReadByUser[] = [];

                const txnId = message.getUnsigned()?.transaction_id;

                let actualMessageId = messageId;
                // If the message still has a temporary ID, try to find the confirmed event
                if (messageId && messageId.startsWith('~')) {
                    const timeline = room.getLiveTimeline();
                    const events = timeline.getEvents();
                    const confirmedEvent = events.find((evt: MatrixEvent) =>
                        evt.getUnsigned()?.transaction_id === txnId &&
                        evt.getId()?.startsWith('$')
                    );
                    if (confirmedEvent) {
                        actualMessageId = confirmedEvent.getId() || messageId;
                    } else {
                        // Message hasn't been confirmed yet, so no one could have read it
                        setReadByUsers([]);
                        return;
                    }
                }

                const timeline = room.getLiveTimeline();
                const events = timeline.getEvents();
                const messageEvents = events.filter((evt: MatrixEvent) => evt.getType() === 'm.room.message');

                    roomMembers.forEach((member: RoomMember) => {
                        if (member.userId === currentUserId || member.userId === message.getSender()) return;
                        if (isServiceParticipant(member.userId)) return;

                        try {
                            const userReceipt = room.getReadReceiptForUserId(member.userId);

                            if (!userReceipt) return;

                            let isLastReadMessage = false;

                            if (userReceipt.eventId === actualMessageId) {
                                isLastReadMessage = true;
                            } else {
                                const receiptEventIndex = events.findIndex((evt: MatrixEvent) => evt.getId() === userReceipt.eventId);

                                if (receiptEventIndex !== -1) {
                                    const receiptEvent = events[receiptEventIndex];
                                    const isReaction = receiptEvent.getType() === 'm.reaction';

                                    if (isReaction) {
                                        const reactionTarget = receiptEvent.getContent()?.['m.relates_to']?.event_id;
                                        if (reactionTarget === actualMessageId) {
                                            isLastReadMessage = true;
                                        }
                                    }
                                }
                            }

                            if (isLastReadMessage) {
                                const currentMessageIndex = messageEvents.findIndex((evt: MatrixEvent) => evt.getId() === actualMessageId);

                                if (currentMessageIndex !== -1) {
                                    const newerMessages = messageEvents.slice(currentMessageIndex + 1);
                                    const hasNewerMessages = newerMessages.some((evt: MatrixEvent) => {
                                        const newerMessageIndex = events.findIndex((e: MatrixEvent) => e.getId() === evt.getId());
                                        const receiptEventIndex = events.findIndex((e: MatrixEvent) => e.getId() === userReceipt.eventId);
                                        return newerMessageIndex !== -1 && receiptEventIndex !== -1 && receiptEventIndex >= newerMessageIndex;
                                    });

                                    if (!hasNewerMessages) {
                                        const avatarUrl = member.getMxcAvatarUrl();
                                        const rawDisplayName = member.rawDisplayName || member.userId;

                                        readUsers.push({
                                            userId: member.userId,
                                            displayName: rawDisplayName,
                                            rawDisplayName,
                                            timestamp: userReceipt.ts || Date.now(),
                                            avatarUrl: avatarUrl || undefined
                                        });
                                    }
                                }
                            }
                        } catch (error) {
                        }
                    });

                    setReadByUsers(readUsers);

                } catch (error) {
                }
            };

        updateReadStatus();

        const handleReceipt = () => updateReadStatus();
        const handleTimelineUpdate = (event: MatrixEvent, room: any) => {
            // Update read receipts when the message gets confirmed (temporary ID → server ID)
            if (room?.roomId === roomId && event.getId() === messageId) {
                updateReadStatus();
            }
        };

        client.on('Room.receipt', handleReceipt);
        client.on('Room.timeline', handleTimelineUpdate);

        return () => {
            client.off('Room.receipt', handleReceipt);
            client.off('Room.timeline', handleTimelineUpdate);
        };
    }, [message, roomId]);


    const formatTimestamp = useCallback((timestamp: number) => {
        return new Date(timestamp).toLocaleTimeString([], {
            hour: '2-digit',
            minute: '2-digit'
        });
    }, []);

    const editedAtLabel = useMemo(() => {
        if (!replacingEventId) return intl.formatMessage({id: 'chat.edit.edited', defaultMessage: 'Edited'});
        const editedAt = message.replacingEventDate();
        return editedAt
            ? intl.formatMessage({id: 'chat.edit.edited-at', defaultMessage: 'Edited {time}'}, {time: formatTimestamp(editedAt.getTime())})
            : intl.formatMessage({id: 'chat.edit.edited', defaultMessage: 'Edited'});
    }, [message, replacingEventId, formatTimestamp, intl]);

    const formatSenderName = useCallback((senderId: string) => {
        const username = senderId.split(':')[0].replace('@', '');
        return username.charAt(0).toUpperCase() + username.slice(1).replace(/_/g, ' ');
    }, []);

    // The chip the user just added bounces once (D-CHAT-01 motion).
    const [justReacted, setJustReacted] = useState<string | null>(null);
    useEffect(() => {
        if (!justReacted) return;
        const timer = window.setTimeout(() => setJustReacted(null), 400);
        return () => window.clearTimeout(timer);
    }, [justReacted]);

    // Only a bubble that arrives while the room is open eases in; history
    // loaded with the room stays still.
    const [isFreshMessage] = useState(() => Date.now() - message.getTs() < 5000);

    const handleReaction = useCallback(async (emojiData: any) => {
        if (isReacting) return;

        setIsReacting(true);
        try {
            setJustReacted(emojiData.emoji);
            await sendReaction(roomId, message.getId()!, emojiData.emoji);
            setShowEmojiPicker(false);
        } catch (error) {
        } finally {
            setIsReacting(false);
        }
    }, [isReacting, sendReaction, roomId, message]);

    const handleReactionClick = useCallback(async (emoji: string) => {
        if (isReacting) return;

        setIsReacting(true);
        try {
            const reaction = reactions.find(r => r.emoji === emoji);

            if (reaction && reaction.hasReacted) {
                await removeReaction(roomId, message.getId()!, emoji);
                setReactions(prev => {
                    const updated = prev.map(r => {
                        if (r.emoji === emoji) {
                            const newUsers = r.users.filter(userId => userId !== currentUserId);
                            const newUserNames = r.userNames.filter((_, index) => r.users[index] !== currentUserId);
                            const newEventIds = r.eventIds.filter((_, index) => r.users[index] !== currentUserId);

                            if (newUsers.length === 0) {
                                return null;
                            }

                            return {
                                ...r,
                                count: newUsers.length,
                                users: newUsers,
                                userNames: newUserNames,
                                eventIds: newEventIds,
                                hasReacted: false
                            };
                        }
                        return r;
                    }).filter(Boolean) as Reaction[];

                    return updated;
                });
            } else {
                setJustReacted(emoji);
                await sendReaction(roomId, message.getId()!, emoji);
            }
        } catch (error) {
        } finally {
            setIsReacting(false);
        }
    }, [isReacting, reactions, removeReaction, sendReaction, roomId, message, currentUserId]);

    const handleReply = useCallback(() => {
        if (onReply) {
            onReply(message.getId()!);
        }
    }, [onReply, message]);

    const canEdit = useMemo(
        () => !!onEdit && !!eventId && isEditableMessage(message, {currentUserId, isRedacted}),
        [onEdit, message, currentUserId, isRedacted, eventId]
    );

    const handleEdit = useCallback(() => {
        if (onEdit) {
            onEdit(message.getId()!);
        }
    }, [onEdit, message]);

    const isSystemLikeEvent = useMemo(() => {
        const eventType = message.getType();
        return eventType === 'm.room.member' || eventType === 'm.room.name' || eventType === 'm.room.topic';
    }, [message]);

    const canReplyPrivately = useMemo(
        () => !!onPrivateReply
            && !isDecryptionErrorMessage
            && !isBotUser(message.getSender() ?? '', currentUserId ?? undefined)
            && canPrivateReply({
                isDirectChat: !isGroupChat,
                myUserId: currentUserId,
                message: {
                    sender: message.getSender() ?? undefined,
                    type: isSystemLikeEvent ? 'system' : 'text',
                    isRedacted,
                },
            }),
        [onPrivateReply, isGroupChat, currentUserId, message, isRedacted, isDecryptionErrorMessage, isSystemLikeEvent]
    );

    const handlePrivateReply = useCallback(() => {
        if (onPrivateReply) {
            onPrivateReply(message);
        }
    }, [onPrivateReply, message]);

    const canForward = !!onForward && !isRedacted && !isDecryptionErrorMessage && !isSystemLikeEvent
        && isForwardableContent(content);
    const handleForward = useCallback(() => onForward?.(message), [onForward, message]);
    const forwardedFrom = useMemo(() => readForwardedFrom(content), [content]);
    const forwardedOriginalAvailable = !!forwardedFrom && !!getClient()?.getRoom?.(forwardedFrom.room_id)
        && getClient()?.getRoom?.(forwardedFrom.room_id)?.getMyMembership?.() === 'join';

    const redactMessage = useCallback(async () => {
        const events = imageGroup && imageGroup.length > 1 ? imageGroup : [message];
        const client = getClient();
        if (!client) return;

        setIsDeleting(true);
        try {
            for (const msg of events) {
                let id = msg.getId();
                if (!id) continue;

                if (id.startsWith('~')) {
                    const room = client.getRoom(roomId);
                    const txn = msg.getUnsigned()?.transaction_id;
                    if (room && txn) {
                        const confirmed = room.getLiveTimeline().getEvents().find(
                            (evt: MatrixEvent) => evt.getUnsigned()?.transaction_id === txn && evt.getId()?.startsWith('$')
                        );
                        if (confirmed) id = confirmed.getId()!;
                    }
                    if (id?.startsWith('~')) {
                        throw new Error('Message is still sending');
                    }
                }

                await matrixService.deleteMessage(roomId, id!);
            }
            setIsRedacted(true);
        } finally {
            setIsDeleting(false);
        }
    }, [imageGroup, message, getClient, roomId]);

    const getCleanedMessageBody = useCallback((body: string, originalEventId?: string): string => {
        if (!originalEventId) return body;

        const lines = body.split('\n');
        const replyStartIndex = lines.findIndex(line => line.startsWith('> '));

        if (replyStartIndex !== -1) {
            let replyEndIndex = replyStartIndex;
            for (let i = replyStartIndex; i < lines.length; i++) {
                if (lines[i].startsWith('> ')) {
                    replyEndIndex = i;
                } else if (lines[i].trim() === '') {
                    break;
                }
            }

            const remainingLines = lines.slice(replyEndIndex + 1);
            while (remainingLines.length > 0 && remainingLines[0].trim() === '') {
                remainingLines.shift();
            }

            return remainingLines.join('\n').trim();
        }

        return body;
    }, []);

    const renderLinkPreview = useCallback((preview: LinkPreview) => {
        const previewImage = preview.image || preview.imageUrl;

        return (
            <a
                key={preview.url}
                href={preview.url}
                target="_blank"
                rel="noopener noreferrer"
                className={`block mt-2 rounded-lg border overflow-hidden transition-all hover:shadow-md ${
                    isOwnMessage
                        ? 'bg-surface/10 border-white/20 hover:bg-surface/15'
                        : 'bg-surface border-line hover:border-fill-subtle'
                }`}
            >
                {previewImage && (
                    <div className="w-full h-48 overflow-hidden">
                        <img
                            src={previewImage}
                            alt={preview.title || 'Link preview'}
                            className="w-full h-full object-cover"
                            onError={(e) => {
                                (e.target as HTMLElement).style.display = 'none';
                            }}
                        />
                    </div>
                )}
                <div className="p-3">
                    <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                            {preview.title && (
                                <h4 className={`font-semibold text-sm line-clamp-2 mb-1 text-ink`}>
                                    {preview.title}
                                </h4>
                            )}
                            {preview.description && (
                                <p className={`text-xs line-clamp-2 mb-2 text-ink-muted-strong`}>
                                    {preview.description}
                                </p>
                            )}
                            <div className={`flex items-center gap-1 text-xs text-ink-muted`}>
                                {preview.favicon && (
                                    <img
                                        src={preview.favicon}
                                        alt=""
                                        className="w-4 h-4 rounded"
                                        onError={(e) => {
                                            (e.target as HTMLElement).style.display = 'none';
                                        }}
                                    />
                                )}
                                <span className="truncate">
                                    {preview.siteName || new URL(preview.url).hostname}
                                </span>
                            </div>
                        </div>
                        <ExternalLink className={`flex-shrink-0 w-4 h-4 text-ink-muted`} />
                    </div>
                </div>
            </a>
        );
    }, [isOwnMessage]);

    const renderPhoneHQMessage = useCallback(() => {
        const { msgtype, body } = content;

        switch (msgtype) {
            case 'org.phonehq.call_invitation':
                const callData = content['org.phonehq.call_data'];
                return (
                    <div className="bg-green-50 border border-green-200 rounded-lg p-4 max-w-md">
                        <div className="flex items-center space-x-3">
                            <div className="flex-shrink-0">
                                <div className="w-10 h-10 bg-green-500 rounded-full flex items-center justify-center">
                                    <PhoneHQGlyph name="phone" size={24} className="text-white"/>
                                </div>
                            </div>
                            <div className="flex-1">
                                <h4 className="font-medium text-green-800">Call Invitation</h4>
                                <p className="text-sm text-green-700">
                                    {callData?.type === 'video' ? 'Video' : 'Voice'} call to {callData?.number}
                                </p>
                            </div>
                            <button className="px-3 py-1 bg-green-600 text-white text-sm rounded hover:bg-green-700 transition-colors">
                                Join Call
                            </button>
                        </div>
                    </div>
                );

            case 'org.phonehq.call_summary':
                const summaryData = content['org.phonehq.call_data'];
                return (
                    <div className="bg-brand border border-brand rounded-lg p-4 max-w-md">
                        <div className="flex items-center space-x-3">
                            <div className="flex-shrink-0">
                                <div className="w-10 h-10 bg-blue-500 rounded-full flex items-center justify-center">
                                    <PhoneHQGlyph name="call_end" size={24} className="text-white"/>
                                </div>
                            </div>
                            <div className="flex-1">
                                <h4 className="font-medium text-blue-800">Call Summary</h4>
                                <p className="text-sm text-blue-700">
                                    {summaryData?.type} call {summaryData?.contactName ? `with ${summaryData.contactName}` : `to ${summaryData?.phoneNumber}`}
                                </p>
                                {summaryData?.duration && (
                                    <p className="text-xs text-blue-600">
                                        Duration: {Math.floor(summaryData.duration / 60)}:{(summaryData.duration % 60).toString().padStart(2, '0')}
                                    </p>
                                )}
                            </div>
                        </div>
                    </div>
                );

            case 'org.phonehq.contact_share':
                const contactData = content['org.phonehq.contact_data'];
                return (
                    <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 max-w-md">
                        <div className="flex items-center space-x-3">
                            <div className="flex-shrink-0">
                                <div className="w-10 h-10 bg-purple-500 rounded-full flex items-center justify-center">
                                    <PhoneHQGlyph name="person" size={24} className="text-white"/>
                                </div>
                            </div>
                            <div className="flex-1">
                                <h4 className="font-medium text-purple-800">Contact Shared</h4>
                                <p className="text-sm text-purple-700">{contactData?.name}</p>
                                <p className="text-xs text-purple-600">{contactData?.phoneNumber}</p>
                            </div>
                            <button className="px-3 py-1 bg-purple-600 text-white text-sm rounded hover:bg-purple-700 transition-colors">
                                Add Contact
                            </button>
                        </div>
                    </div>
                );

            case 'org.phonehq.schedule_callback':
                const scheduleData = content['org.phonehq.schedule_data'];
                return (
                    <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 max-w-md">
                        <div className="flex items-center space-x-3">
                            <div className="flex-shrink-0">
                                <div className="w-10 h-10 bg-orange-500 rounded-full flex items-center justify-center">
                                    <PhoneHQGlyph name="schedule" size={24} className="text-white"/>
                                </div>
                            </div>
                            <div className="flex-1">
                                <h4 className="font-medium text-orange-800">Callback Scheduled</h4>
                                <p className="text-sm text-orange-700">
                                    Callback for {scheduleData?.phoneNumber}
                                </p>
                                {scheduleData?.scheduledTime && (
                                    <p className="text-xs text-orange-600">
                                        Scheduled: {new Date(scheduleData.scheduledTime).toLocaleString([], {
                                        hour12: true,
                                        hour: '2-digit',
                                        minute: '2-digit',
                                        month: 'short',
                                        day: 'numeric',
                                        year: 'numeric'
                                    }).replace(/AM|PM/g, match => match.toLowerCase())}
                                    </p>
                                )}
                            </div>
                        </div>
                    </div>
                );

            default:
                const cleanedBody = getCleanedMessageBody(body, replyInfo?.originalMessageId);
                return (
                    <>
                        <div className="text-ink break-words text-wrap message-content">
                            {renderTextWithMentions(cleanedBody)}
                        </div>
                        {/* Render link previews for text messages */}
                        {linkPreviews.length > 0 && (
                            <div className="space-y-2">
                                {linkPreviews.map(preview => renderLinkPreview(preview))}
                            </div>
                        )}
                        {loadingPreviews && (
                            <div className={`mt-2 text-xs text-ink-muted`}>
                                Loading preview...
                            </div>
                        )}
                    </>
                );
        }
    }, [content, getCleanedMessageBody, replyInfo, renderTextWithMentions, currentUserId, linkPreviews, loadingPreviews, renderLinkPreview, isOwnMessage]);

    const isNotMedia = !isMediaMessageType(content?.msgtype);
    const isTextLikeMessage = content?.msgtype === 'm.text' || isDecryptionErrorMessage;
    const textBubbleClasses = isOwnMessage
        ? `bg-brand-tint rounded-br-tail text-ink ${
            !isFirstInGroup ? 'rounded-tr-md' : ''
        } ${
            !isLastInGroup ? 'rounded-br-md' : ''
        }`
        : `bg-surface-muted rounded-bl-tail text-ink ${
            !isFirstInGroup ? 'rounded-tl-md' : ''
        } ${
            !isLastInGroup ? 'rounded-bl-md' : ''
        }`;
    const mediaAlignmentClass = isOwnMessage ? 'ml-auto' : 'mr-auto';
    const mediaCaptionClassName = `mt-1 px-4 py-2 rounded-card w-fit max-w-bubble break-words [overflow-wrap:anywhere] whitespace-pre-wrap ${mediaAlignmentClass} ${textBubbleClasses}`;
    const groupedImages = useMemo<ImageInfo[]>(() => {
        if (!imageGroup || imageGroup.length <= 1) return [];

        return imageGroup.map(msg => {
            const groupedContent = msg.getContent();
            const groupedInfo = groupedContent.info;
            return {
                url: groupedContent.url || groupedContent.file?.url || '',
                alt: groupedContent.body || 'Image',
                encryptedFile: groupedContent.file,
                mimeType: groupedInfo?.mimetype,
                thumbnailUrl: groupedInfo?.thumbnail_url || groupedInfo?.thumbnail_file?.url,
                thumbnailEncryptedFile: groupedInfo?.thumbnail_file,
                thumbnailWidth: groupedInfo?.thumbnail_info?.w,
                thumbnailHeight: groupedInfo?.thumbnail_info?.h,
            };
        });
    }, [imageGroup]);
    const groupedImageCaptions = useMemo(() => {
        if (!imageGroup || imageGroup.length <= 1) return [];

        return imageGroup
            .map((msg, index) => {
                const captionText = getMediaCaption(msg.getContent() as Record<string, unknown>);
                if (!captionText) {
                    return null;
                }

                return {
                    captionText,
                    content: msg.getContent() as Record<string, unknown>,
                    key: msg.getId() || `caption-${index}`,
                };
            })
            .filter((caption): caption is { captionText: string; content: Record<string, unknown>; key: string } => caption !== null);
    }, [imageGroup]);
    const handleGroupedImageClick = useCallback((index: number) => {
        if (onOpenImageGallery) {
            onOpenImageGallery(groupedImages, index);
        }
    }, [groupedImages, onOpenImageGallery]);

    const renderMessageContent = useCallback(() => {
        const { msgtype, info } = content;
        const url = content.url || content.file?.url || '';
        const encryptedFile = content.file;
        const body = displayBody;
        const mediaCaption = getMediaCaption(content as Record<string, unknown>);

        if (msgtype?.startsWith('org.phonehq.')) {
            return renderPhoneHQMessage();
        }


        if (imageGroup && imageGroup.length > 1) {
            return (
                <>
                    <ImageGrid
                        images={groupedImages}
                        onImageClick={handleGroupedImageClick}
                        isOwnMessage={isOwnMessage}
                        onImageLoad={onMediaLoad}
                    />
                    {groupedImageCaptions.map((caption) => (
                        <div
                            key={caption.key}
                            className={mediaCaptionClassName}
                        >
                            {renderTextWithMentions(caption.captionText, caption.content)}
                        </div>
                    ))}
                </>
            );
        }

        switch (msgtype) {
            case 'm.image': {
                const thumbnailUrl = info?.thumbnail_url || info?.thumbnail_file?.url;
                return (
                    <div className={`w-fit max-w-sm ${mediaAlignmentClass}`}>
                        <div
                            className={`${isOwnMessage ? 'ml-auto' : 'mr-auto'} relative w-fit cursor-pointer rounded-lg overflow-hidden group`}
                            onClick={() => setSingleImageGallery([{
                                url,
                                alt: body,
                                encryptedFile,
                                mimeType: info?.mimetype,
                                thumbnailUrl,
                                thumbnailEncryptedFile: info?.thumbnail_file,
                                thumbnailWidth: info?.thumbnail_info?.w,
                                thumbnailHeight: info?.thumbnail_info?.h,
                            }])}
                        >
                            <AuthenticatedImage
                                src={url}
                                alt={body}
                                encryptedFile={encryptedFile}
                                mimeType={info?.mimetype}
                                className="block max-w-full h-auto rounded-lg transition-transform duration-200 group-hover:scale-105"
                                style={{ maxHeight: '300px' }}
                                loading="lazy"
                                onLoad={onMediaLoad}
                                width={info?.w}
                                height={info?.h}
                                thumbnailSrc={thumbnailUrl}
                                thumbnailEncryptedFile={info?.thumbnail_file}
                                thumbnailWidth={info?.thumbnail_info?.w}
                                thumbnailHeight={info?.thumbnail_info?.h}
                            />
                            <div className="absolute inset-0 bg-black bg-opacity-0 hover:bg-opacity-10 transition-all duration-200 flex items-center justify-center">
                                <PhoneHQGlyph name="zoom_in" size={24} className="text-white opacity-0 group-hover:opacity-100"/>
                            </div>
                        </div>
                        {mediaCaption && (
                            <div className={mediaCaptionClassName}>{renderTextWithMentions(mediaCaption)}</div>
                        )}
                    </div>
                );
            }

            case 'm.video':
                return (
                    <div className={`w-fit max-w-sm ${mediaAlignmentClass}`}>
                        <AuthenticatedVideo
                            src={url}
                            filename={content.filename || content.body}
                            encryptedFile={encryptedFile}
                            mimeType={info?.mimetype}
                            className="max-w-full rounded-lg"
                            style={{ maxHeight: '300px' }}
                            controls={true}
                            preload="metadata"
                            onLoadedMetadata={onMediaLoad}
                            thumbnailSrc={info?.thumbnail_url || info?.thumbnail_file?.url}
                            thumbnailEncryptedFile={info?.thumbnail_file}
                            thumbnailMimeType={info?.thumbnail_info?.mimetype}
                            width={info?.w}
                            height={info?.h}
                            thumbnailWidth={info?.thumbnail_info?.w}
                            thumbnailHeight={info?.thumbnail_info?.h}
                            durationMs={info?.duration}
                            sizeBytes={info?.size}
                        />
                        {mediaCaption && (
                            <div className={mediaCaptionClassName}>{renderTextWithMentions(mediaCaption)}</div>
                        )}
                    </div>
                );

            case 'm.audio': {
                const canShowTranscript = isAiBotEnabled && isAiBotInRoom
                    && (canRequestTranscript || isTranscribing || !!transcriptText || !!transcriptError);
                const toggleTranscript = async () => {
                    if (transcriptText || transcriptError || isTranscribing || transcription.status) {
                        setShowTranscriptModal(previous => !previous);
                        return;
                    }
                    const audioEventId = message.getId();
                    if (!canRequestTranscript || !audioEventId?.startsWith('$')) return;
                    setShowTranscriptModal(true);
                    await transcription.start({kind: 'transcription', audioEventId});
                };
                return (
                    <div className={`w-fit max-w-sm ${mediaAlignmentClass}`}>
                        <VoiceMessagePlayer
                            src={url}
                            seed={message.getId() ?? url}
                            encryptedFile={encryptedFile}
                            mimeType={info?.mimetype}
                            durationMs={typeof info?.duration === 'number' ? info.duration : undefined}
                            isOwn={isOwnMessage}
                        >
                            {/* D-CHAT-08/S07–S08: transcript on demand, inside the bubble. */}
                            {canShowTranscript && showTranscriptModal && (
                                <div className="flex flex-col gap-1.5 border-t border-ink/10 pt-2">
                                    <TranscriptInline
                                        isOpen
                                        transcript={transcriptText}
                                        isLoading={isTranscribing}
                                        error={transcriptError}
                                        statusText={transcription.status && !transcriptText && transcription.status.state !== 'failed'
                                            ? aiStatusText(transcription.status) : undefined}
                                        onRetry={transcription.status?.retryAllowed && canRequestTranscript && !transcriptText
                                            ? () => { void transcription.retry(); } : undefined}
                                        onRecover={transcription.transportError ? () => { void transcription.recover(); } : undefined}
                                        sending={transcription.sending}
                                    />
                                </div>
                            )}
                            {canShowTranscript && (
                                <div className="flex items-center justify-between gap-2">
                                    <span className="text-xs text-ink-muted">
                                        {showTranscriptModal && transcriptText ? 'Automatic transcript' : ''}
                                    </span>
                                    <button type="button" aria-expanded={showTranscriptModal}
                                            data-testid="chat.message.transcript-toggle"
                                            onClick={() => { void toggleTranscript(); }}
                                            className="inline-flex h-6 items-center gap-1 text-[13px] font-semibold text-brand-ink">
                                        {isTranscribing && !transcriptText
                                            ? 'Transcribing…'
                                            : showTranscriptModal ? 'Hide transcript' : 'Show transcript'}
                                        {showTranscriptModal
                                            ? <ChevronUp size={14} aria-hidden="true" className="!p-0 !text-brand-ink"/>
                                            : <ChevronDown size={14} aria-hidden="true" className="!p-0 !text-brand-ink"/>}
                                    </button>
                                </div>
                            )}
                        </VoiceMessagePlayer>
                        {mediaCaption && (
                            <div className={mediaCaptionClassName}>{renderTextWithMentions(mediaCaption)}</div>
                        )}
                    </div>
                );
            }

            case 'm.file':
                return (
                    <div className={`w-fit min-w-0 ${mediaAlignmentClass}`}>
                        <AuthenticatedFile
                            src={url}
                            filename={content.filename || body}
                            mimeType={info?.mimetype}
                            size={info?.size}
                            encryptedFile={encryptedFile}
                            className="cursor-pointer"
                        />
                        {mediaCaption && (
                            <div className={mediaCaptionClassName}>{renderTextWithMentions(mediaCaption)}</div>
                        )}
                    </div>
                );

            default:
                const cleanedBody = getCleanedMessageBody(body, replyInfo?.originalMessageId);
                const textNode = (searchQuery && highlightedMessageId === message.getId())
                    ? highlightSearchTerm(cleanedBody)
                    : renderTextWithMentions(cleanedBody);
                return (
                    <>
                        <MessageTextContent
                            textNode={textNode}
                            isOwnMessage={isOwnMessage}
                            isDecryptionErrorMessage={isDecryptionErrorMessage}
                            decryptionErrorTooltipProps={decryptionErrorTooltipProps}
                        />
                        {linkPreviews.length > 0 && (
                            <div className="space-y-2">
                                {linkPreviews.map(preview => renderLinkPreview(preview))}
                            </div>
                        )}
                        {loadingPreviews && (
                            <div className={`mt-2 text-xs text-ink-muted`}>
                                Loading preview...
                            </div>
                        )}
                    </>
                );
        }
    }, [content, displayBody, renderPhoneHQMessage, getCleanedMessageBody, replyInfo, isOwnMessage, renderTextWithMentions,currentUserId, linkPreviews, loadingPreviews, renderLinkPreview, searchQuery, highlightSearchTerm, highlightedMessageId, message, imageGroup, onMediaLoad, isDecryptionErrorMessage, decryptionErrorTooltipProps, mediaAlignmentClass, mediaCaptionClassName, transcriptText, isTranscribing, showTranscriptModal, transcriptError, groupedImages, handleGroupedImageClick, groupedImageCaptions, isAiBotEnabled, isAiBotInRoom, canRequestTranscript, transcription]);

    const renderReactions = useCallback(() => {
        if (reactions.length === 0) return null;

        return (
            <div className={`mt-1 flex flex-wrap gap-1 ${isOwnMessage ? 'justify-end' : 'justify-start'}`}>
                {reactions.map((reaction) => (
                    <button
                        key={reaction.emoji}
                        onClick={() => handleReactionClick(reaction.emoji)}
                        disabled={isReacting}
                        data-testid="chat.message.reaction-chip"
                        aria-pressed={reaction.hasReacted}
                        className={`inline-flex h-6 items-center gap-1 rounded-xl border px-2 text-[13px] font-semibold transition-colors ${
                            reaction.hasReacted
                                ? 'border-brand-disabled bg-brand-tint-subtle text-brand-ink hover:bg-brand-tint'
                                : 'border-line bg-surface text-ink-secondary hover:bg-surface-muted'
                        } ${isReacting ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'} ${justReacted === reaction.emoji ? 'phq-anim-chip' : ''}`}
                        {...(
                            isOwnMessage
                                ? { "data-tooltip-left": `${reaction.userNames.join(', ')} reacted with ${reaction.emoji}` }
                                : { "data-tooltip-right": `${reaction.userNames.join(', ')} reacted with ${reaction.emoji}` }
                        )}
                    >
                        <span>{reaction.emoji}</span>
                        <span>{reaction.count}</span>
                    </button>
                ))}
            </div>
        );
    }, [reactions, isOwnMessage, handleReactionClick, isReacting, justReacted]);

    const emojiPickerStyles = useMemo(() => {
        if (!messageBubbleRef.current) {
            return {};
        }

        try {
            const rect = messageBubbleRef.current.getBoundingClientRect();

            return {
                [emojiPickerPosition === 'bottom' ? 'top' : 'bottom']: emojiPickerPosition === 'bottom'
                    ? `${rect.bottom}px`
                    : `${window.innerHeight - rect.top}px`,
                [isOwnMessage ? 'right' : 'left']: isOwnMessage
                    ? `${window.innerWidth - rect.right}px`
                    : `${rect.left}px`
            };
        } catch {
            return {};
        }
    }, [emojiPickerPosition, isOwnMessage, showEmojiPicker]);

    const handleEmojiPickerToggle = useCallback(() => {
        if (!showEmojiPicker && messageBubbleRef.current) {
            try {
                const bubbleRect = messageBubbleRef.current.getBoundingClientRect();
                const viewportHeight = window.innerHeight;
                const EMOJI_PICKER_HEIGHT = 400;
                const SPACING_BUFFER = 10;

                const spaceBelow = viewportHeight - bubbleRect.bottom - SPACING_BUFFER;
                const spaceAbove = bubbleRect.top - SPACING_BUFFER;

                if (spaceBelow < EMOJI_PICKER_HEIGHT && spaceAbove > spaceBelow) {
                    setEmojiPickerPosition('top');
                } else {
                    setEmojiPickerPosition('bottom');
                }
            } catch (error) {
                setEmojiPickerPosition('bottom');
            }
        }
        setShowEmojiPicker(!showEmojiPicker);
    }, [showEmojiPicker]);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (showEmojiPicker) {
                const target = event.target as Element;
                if (!target.closest('.emoji-picker-container') && !target.closest('.emoji-button')) {
                    setShowEmojiPicker(false);
                }
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [showEmojiPicker]);

    if (message.getType() === 'm.reaction') {
        return null;
    }

    if (!userInfo) {
        return null;
    }
    const deleteForEveryoneLabel = isNotMedia ? 'Delete message for everyone' : 'Delete file for everyone';
    return (
        <div  className={`flex flex-col ${message.getContent()?.['m.file-text'] ? '' : `space-y-1 ${isFirstInGroup ? '!mt-6' : ''}`} ${isOwnMessage ? 'items-end' : 'items-start'} ${isOwnMessage ? 'message-bubble-right' : 'message-bubble-left'}`}>
            {showTimestamp && (
                <div className={`mt-1 text-xs text-ink-muted ${isOwnMessage ? 'text-right' : 'text-left ml-10'}`}>
                    <span className="mx-1">{(!isOwnMessage && isGroupChat ) ? userInfo.displayName +  ' ·' : '' }</span>
                    <span>{ formatTimestamp(message.getTs())}</span>
                </div>
            )}

            {replyInfo && (
                <div onClick={()=> scrollToEvent(replyInfo?.originalMessageId)} className={`flex items-center gap-[10px] text-tiny bg-brand-tint-subtle rounded-lg  pl-3 pr-4 py-2 border border-brand-focus max-w-[50%] ${
                    isOwnMessage ? '' : 'ml-9'
                } ${typeof replyInfo.originalContent === 'string' ? 'truncate' : ''}`}>
                    <Reply className={'text-brand-ink'} size={14} />
                    <div className={typeof replyInfo.originalContent === 'string' ? 'max-w-[90%]' : 'flex-1'}>
                        <div className="flex items-center space-x-1 text-brand-ink">
                            <span className="font-medium text-brand-ink ">{replyInfo.originalSenderName}</span>
                            {replyInfo.originalIsEdited && (
                                <Pencil
                                    data-testid="quoted-message-edited-marker"
                                    className="text-brand-ink"
                                    size={11}
                                />
                            )}
                        </div>
                        {typeof replyInfo.originalContent === 'string' ? (
                            <p className="text-brand-ink font-medium truncate message-content ">
                                {replyInfo.originalContent.substring(0, 100)}
                                {replyInfo.originalContent.length > 100 ? '...' : ''}
                            </p>
                        ) : (
                            <div className="text-brand-ink font-medium message-content overflow-hidden">
                                {replyInfo.originalContent}
                            </div>
                        )}
                    </div>
                </div>
            )}

            <div className={`flex w-full items-end space-x-1 ${isOwnMessage ? 'justify-end' : 'justify-start'}`}>
                {!isOwnMessage && (
                    <PhoneHQTooltip content={userInfo.displayName} side="top"><button
                            type="button"
                            className={`w-8 h-8 rounded-lg flex-shrink-0 self-end ${isServiceParticipant(userInfo.userId) ? 'cursor-default' : 'cursor-pointer'}`}
                            onClick={() => !isServiceParticipant(userInfo.userId) && onViewProfile?.(userInfo.userId)}
                            aria-label={`View ${userInfo.displayName}'s profile`}
                        >
                            <span className="block w-8 h-8 rounded-lg overflow-hidden">
                                <MatrixAvatar name={userInfo.rawDisplayName} mxcUrl={userInfo.avatarUrl} size={'32px'}/>
                            </span>
                        </button></PhoneHQTooltip>
                )}


                <div className={`flex-1 min-w-0`}>
                    <div className={`flex group items-center ${isOwnMessage ? 'justify-end' : 'justify-end flex-row-reverse'} ${isFreshMessage ? 'phq-anim-bubble' : ''}`}>
                        {!isRedacted && (
                            <div
                                className={`opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex ${isOwnMessage ? 'mr-2' : 'ml-2'}`}>
                                {isOwnMessage && (
                                    <PhoneHQDropdown
                                        side="bottom"
                                        showCloseIcon={true}
                                        trigger={
                                            <button
                                                className={'flex'}
                                                data-tooltip="Message actions"
                                                disabled={isDeleting}
                                            >
                                                <MoreVertical className="aligin-center p-1.5" size={28}/>
                                            </button>
                                        }
                                    >
                                        {canEdit && (
                                            <PhoneHQDropdown.Item onClick={handleEdit} data-testid="chat.message.edit-menu-item">
                                                <Pencil className="mr-3 h-4 w-4"/>
                                                <FormattedMessage id="chat.edit.menu-item" defaultMessage="Edit"/>
                                            </PhoneHQDropdown.Item>
                                        )}
                                        <PhoneHQDropdown.Item onClick={() => setShowDeleteDialog(true)}>
                                            <Trash2 className="mr-3 h-4 w-4"/>
                                            {deleteForEveryoneLabel}
                                        </PhoneHQDropdown.Item>
                                    </PhoneHQDropdown>
                                )}
                                <button
                                    className="emoji-button"
                                    onClick={handleEmojiPickerToggle}
                                    data-tooltip="Add reaction"
                                    disabled={isReacting}
                                >
                                    <Smile className="p-1.5" size={28}/>
                                </button>
                                <button
                                    onClick={handleReply}
                                    data-tooltip="Reply"
                                >
                                    <Reply className="p-1.5" size={28}/>
                                </button>
                                {canForward && (
                                    <button
                                        onClick={handleForward}
                                        data-tooltip="Forward"
                                        aria-label="Forward"
                                        data-testid="chat.message.forward-btn"
                                    >
                                        <Forward className="p-1.5" size={28}/>
                                    </button>
                                )}
                                {canReplyPrivately && (
                                    <button
                                        onClick={handlePrivateReply}
                                        data-tooltip={intl.formatMessage({id: 'chat.private-reply.action', defaultMessage: 'Reply privately'})}
                                        aria-label={intl.formatMessage({id: 'chat.private-reply.action', defaultMessage: 'Reply privately'})}
                                    >
                                        <MessageSquareLock className="p-1.5" size={28}/>
                                    </button>
                                )}
                            </div>
                        )}

                        <div className={`flex flex-col min-w-0 max-w-bubble ${isOwnMessage ? 'order-2 items-end' : 'order-1 items-start'}`}>
                            <div
                                ref={messageBubbleRef}
                                id={`message-${message.getId()}`}
                                onClick={handleCodeBlockClick}
                                className={`message-bubble max-w-full min-w-0 ${highlightedMessageId === message.getId() ? 'highlight-message' : ''} relative rounded-card py-2.5 px-3.5 text-callout ${
                                    isRedacted
                                        ? (isOwnMessage ? 'bg-brand-tint rounded-br-tail text-ink' : 'bg-surface-muted rounded-bl-tail text-ink')
                                        : `${isNotMedia ? '' : '!py-0 !px-0'} ${isTextLikeMessage ? textBubbleClasses : ''}`
                                }`}>
                                {isRedacted ? (
                                    <div className={`flex items-center gap-2 text-sm text-ink-secondary`}>
                                        <Trash2 size={14} className={`text-ink-secondary`}/>
                                        <span>Message deleted</span>
                                    </div>
                                ) : (
                                    <>
                                        {forwardedFrom && (
                                            forwardedOriginalAvailable ? (
                                                <button type="button" data-testid="chat.message.forwarded-label"
                                                        onClick={() => onOpenForwardedOriginal?.(forwardedFrom.room_id, forwardedFrom.event_id)}
                                                        aria-label={`${forwardedLabel(forwardedFrom)}. Open original`}
                                                        className="mb-1 flex items-center gap-1 text-[13px] font-semibold text-brand-ink hover:underline">
                                                    <Forward size={14} aria-hidden="true" className="!p-0 !text-brand-ink"/>
                                                    {forwardedLabel(forwardedFrom)}
                                                </button>
                                            ) : (
                                                <div data-testid="chat.message.forwarded-label"
                                                     className="mb-1 flex items-center gap-1 text-[13px] font-semibold text-ink-muted-strong">
                                                    <Forward size={14} aria-hidden="true" className="!p-0 !text-ink-muted-strong"/>
                                                    {forwardedLabel(forwardedFrom)}
                                                </div>
                                            )
                                        )}
                                        {renderMessageContent()}

                                        {isEdited && (
                                            <div
                                                data-testid="message-edited-marker"
                                                title={editedAtLabel}
                                                className={`mt-0.5 flex items-center gap-1 text-tiny text-ink-muted`}
                                            >
                                                <Pencil size={11}/>
                                                <span><FormattedMessage id="chat.edit.marker" defaultMessage="(edited)"/></span>
                                            </div>
                                        )}

                                        {showEmojiPicker && messageBubbleRef.current && (
                                            <div
                                                className={`emoji-picker-container phq-anim-pop fixed z-50 shadow-lg ${emojiPickerPosition === 'top' ? 'origin-bottom' : 'origin-top'}`}
                                                style={emojiPickerStyles}
                                            >
                                                <EmojiPicker
                                                    emojiStyle={EmojiStyle.TWITTER}
                                                    onEmojiClick={handleReaction}
                                                    reactionsDefaultOpen={true}
                                                    reactions={QUICK_REACTIONS}
                                                    theme={Theme.DARK}
                                                    searchDisabled={false}
                                                    skinTonesDisabled={true}
                                                />
                                            </div>
                                        )}
                                    </>
                                )}
                            </div>

                            {!isRedacted && (
                                <div>
                                    {renderReactions()}
                                </div>
                            )}
                        </div>

                    </div>

                </div>

            </div>
            {renderDeliveryStatus(message.status || EventStatus.SENT)}

            {singleImageGallery && (
                <ImageGallery
                    images={singleImageGallery}
                    initialIndex={0}
                    onClose={() => setSingleImageGallery(null)}
                />
            )}

            {expandedCodeBlock !== null && (
                <CodeBlockViewer
                    text={expandedCodeBlock}
                    onClose={() => setExpandedCodeBlock(null)}
                />
            )}

            <DeleteMessageDialog
                open={showDeleteDialog}
                onClose={() => setShowDeleteDialog(false)}
                onConfirm={redactMessage}
                isDeleting={isDeleting}
                label={deleteForEveryoneLabel}
            />
        </div>
    );
});

MessageBubble.displayName = 'MessageBubble';

export default MessageBubble;
